
import { Component, Emit, Mixins, Watch, Prop } from "vue-property-decorator";
import { namespace, Getter } from "vuex-class";
import NavHeader from "@/components/NavHeader.vue";
import draggable from "vuedraggable";
import vSelect from "vue-select";
import { required, helpers } from 'vuelidate/lib/validators'
import { cloneDeep } from "lodash";
import { validationMixin } from "vuelidate";
import { CloseEventType, SearchDeviceListQueryParam, Tour, TourDevices, TourDevicesPagedResponse, TourDevicesPaginatedQueryParams, TourSteps } from "@/store/tours/types"
import AreaTreeSelect from './form/AreaTreeSelect.vue';
import { removeNonDigitsFromString } from "@/utils/pagination-helper";
import { debounce } from "lodash";
import { getDebouncePeriod } from "@/utils/debounce-helper";
import { TourSetUpConstants } from "@/types/Constants";
import { PageSize } from "@/types/PaginationConstants";
import { Modules } from "@/store/ModuleTypes";
import { NotificationOptionsType } from "@/types/NotificationOptionsType";
import PaginatedSearch, { PaginatedSearchRequest } from "@/mixins/PaginatedSearch";
import { GroupWithPermissions } from "@/store/areas/types"
import { CancelTokenSource } from "axios";
import { CancellableQuery } from "@/store/types";
import { getNewCancelTokenSource } from "@/utils/cancellableQueryHelper";
import { DevicesMaxLength } from "@/types/sv-data/devices/Constants";

const MIN_DURATION = 5;
const MAX_DURATION = 32767; //Max number of the database field

const Areas = namespace(Modules.Areas);
const Tours = namespace(Modules.Tours);

const requiredValidator = helpers.withParams(
	{ type: "required" },
	function(val) {
		return this.tourUsingDuration ? !!val : true;
	}
)

const minValidator = (minVal: number) => helpers.withParams(
	{ type: "min", value: minVal },
	function(val) {
		return this.tourUsingDuration && val ? val >= minVal : true;
	}
)

const maxValidator = (maxVal: number) => helpers.withParams(
	{ type: "max", value: maxVal },
	function(val) {
		return this.tourUsingDuration && val ? val <= maxVal : true;
	}
)

const defaultMatrixOption: TourSetupMatrixOptions = {
	title: "3 x 3",
    matrixColumnSize: 3,
    matrixRowSize: 3
}

interface TourWithMatrixOptions extends Tour {
	matrixOptions?: TourSetupMatrixOptions
}

interface TourSetupMatrixOptions {
    title: string,
    matrixColumnSize: number,
    matrixRowSize: number
}

@Component({
	components: {
		draggable,
		"vue-select": vSelect,
		"nav-header": NavHeader,
        "area-tree-select": AreaTreeSelect,
	},
	mixins: [validationMixin],
	validations: {
		hasAvailableTourDeviceSlots: {
			validator: function(value) {
				// This will make validation pass only when pendingTourDevicesCount >= 0
				return value === true;
      		}
    	},
		currentTour: {
			title: {
				required,
			},
			tourSteps: {
				required,
				$each: {
					duration: {
						required: requiredValidator,
						min: minValidator(MIN_DURATION),
						max: maxValidator(MAX_DURATION)
					}
				}
			}
		}
	}
})
export default class TourSetupModal extends Mixins(PaginatedSearch) {
	/** Store Getter/Setter */
	@Getter getUserTenantGroupId: number;

	@Tours.Action fetchAllTours: () => Tour[];
	@Tours.Action fetchDeviceList: (groupID: number) => TourDevices[];
	@Tours.Action searchDeviceList: (payload: CancellableQuery<SearchDeviceListQueryParam>) => Promise<TourDevices[]>;
	@Tours.Action addTour: (payload: CancellableQuery<Tour>) => Promise<void>;
	@Tours.Action updateTour: (payload: CancellableQuery<Tour>) => Promise<void>;
	@Tours.Action getMissingTourDevices: (payload: PaginatedSearchRequest) => Promise<TourDevicesPagedResponse>;
	@Tours.Action getMaxCamerasAllowedForArea: (groupId: number) => Promise<number>;

	@Tours.Getter getTour: (tourId: number) => Tour;
	@Tours.Getter getDeviceList: TourDevices[];
	@Tours.Getter getSearchDevices: TourDevices[];

	@Tours.Mutation addSearchDevices: (data: TourDevices[]) => void;
	@Tours.Mutation setAllDevices: (data: TourDevices[]) => void;

	@Areas.Action getAreaList: () => GroupWithPermissions[];
	@Areas.Getter("getAreaList") areaList!: GroupWithPermissions[];

	@Prop({default: 0, type: Number})
	private tourId: number;
	@Prop({default: false, type: Boolean})
	private closeModal: boolean;
	@Prop({default: false, type: Boolean})
	private saveTour: boolean;

	private readonly maxLengthTitle: number = DevicesMaxLength.Title;

	private saveTourInProgress: boolean = false;
	private isLoadingMissingDevices: boolean = false;
	private tourUsingDuration: boolean = true;
	private deletingIndex: number = -1;
	private deletingConfirmationIndex: number = -1;
	private tourGroupId: number = 0;
	private searchableGroupId: number = 0;
	private maxCamerasAllowedPerTour: number = TourSetUpConstants.MaxCamerasAllowedPerTour;

	// notify titles
	private readonly notifyTitleTourUpdate: string = "Update Tour";
	private readonly notifyTitleTourDeviceUpdate: string = "Update Tour Devices";
	private readonly notifyTitleTourDeviceUpdateError: string = `${this.notifyTitleTourDeviceUpdate} - Error`;

	// Api search calls
	private deviceSearchValue: string = "";
	private addOrUpdateTourCancelTokenSource: CancelTokenSource = null;
	private searchDeviceListCancelTokenSource: CancelTokenSource = null;

	// selected devices table search
	private tableSearchValue: string = "";

	// pagination
	private readonly defaultPageNumber: number = 1;

	// tour steps table pagination
	private readonly recordsPageSizeMin: number = PageSize.Min;
	private readonly recordsPageSizeMax: number = PageSize.Max;
	private readonly recordsPageSizeDefault: string = PageSize.Default.toString();
	private currentPage: number = this.defaultPageNumber;
	private devicesTablePageSize: number = PageSize.Default; // Number of items per page
	private devicesTablePageSizeUpdated : string = this.recordsPageSizeDefault;

	private currentTour: TourWithMatrixOptions = {
		groupId: 0,
		title: "",
		tourId: 0,
		tourOrder: 0,
		tourSteps: [],
		functionality: null,
		tourStepsCount: 0,
		matrixRowSize: 3,
		matrixColumnSize: 3,
		matrixOptions: defaultMatrixOption
    }

	private matrixOptions: TourSetupMatrixOptions[] = [
		{
			title: "1 x 1",
			matrixColumnSize: 1,
			matrixRowSize: 1
		},
		{
			title: "2 x 2",
			matrixColumnSize: 2,
			matrixRowSize: 2
		},
		{
			title: "2 x 3",
			matrixColumnSize: 2,
			matrixRowSize: 3
		},
		{
			title: "3 x 3",
			matrixColumnSize: 3,
			matrixRowSize: 3
		},
		{
			title: "4 x 3",
			matrixColumnSize: 4,
			matrixRowSize: 3
		},
		{
			title: "4 x 4",
			matrixColumnSize: 4,
			matrixRowSize: 4
		},
	];

    private async mounted(): Promise<void> {
		this.searchableGroupId = this.getUserTenantGroupId;
		this.tourGroupId = this.getUserTenantGroupId;

		if (this.tourId > 0) {
			this.editTourDetails(this.tourId);
		} else {
			this.tourUsingDuration = true;
			this.resetCurrentTour();
		}
	}

	private destroy(): void {
		this.resetSearchDevices();
		this.setAllDevices([]);
	}

	@Emit("close")
	private close(isPostSave: boolean = false): CloseEventType {
		if (this.saveTourInProgress) {
			this.$notify({
				type: NotificationOptionsType.warn,
				title: this.notifyTitleTourUpdate,
				text: "Save tour is in progress"
			});
			return CloseEventType.SaveInProgress;
		}

		if (isPostSave) {
			return CloseEventType.PostSave;
		}

		return CloseEventType.Default;
	}

	@Watch("tourGroupId")
	private async updateMaxCamerasAllowedLimit(): Promise<void> {
		this.maxCamerasAllowedPerTour = await this.getMaxCamerasAllowedForArea(this.tourGroupId);
	}

	/** Method to fetch devices depending on the group you are currently on. */
	@Watch("searchableGroupId")
	private async updateDevicesList(): Promise<void> {
		await this.fetchDeviceList(this.searchableGroupId ? this.searchableGroupId : this.getUserTenantGroupId);
	}

	/** Loads the desired tour into the edit modal */
	@Watch("tourId")
	private editTourDetails(tourId: number): void {
		this.currentTour = this.getTour(tourId);
		if(!this.currentTour){
			this.resetCurrentTour();
		}

		var matrixOptions = this.matrixOptions
			.find(x => x.matrixColumnSize == this.currentTour.matrixColumnSize
					&& x.matrixRowSize == this.currentTour.matrixRowSize
			);

		this.currentTour.matrixOptions = matrixOptions ? matrixOptions :  defaultMatrixOption;
		this.tourGroupId = this.currentTour.groupId ? this.currentTour.groupId : this.getUserTenantGroupId;

		// steps based on confirmation is based on null, else duration is a number
		if (this.currentTour.tourSteps.length > 0)
		{
			this.currentTour.tourSteps[0].duration == 0 ? this.tourUsingDuration = false : this.tourUsingDuration = true;
		}
    }

	@Watch("closeModal")
	private closeModalUpdated(value: boolean): void {
		if (this.closeModal) {
			this.close();
		}
	}

	/** Save new or update tour */
	@Watch("saveTour")
	private async saveTourUpdated(value: boolean): Promise<void> {
		if (this.saveTour) {
			this.saveTourInProgress = true;
			try {
				this.currentTour.tourStepsCount = this.currentTour.tourSteps.length;
				this.currentTour.groupId = this.tourGroupId > 0 ? this.tourGroupId : this.getUserTenantGroupId;
				// If the tourUsingDuration is set to false i.e. confirmation reset the step duration to 3
				if(this.currentTour && this.currentTour.tourSteps && this.currentTour.tourSteps.length > 0 && !this.tourUsingDuration) {
					this.currentTour.tourSteps.forEach(step => {
						step.duration = 0;
					});
				}

				// new tour.
				if (this.currentTour.tourId == 0) {
					const payload: CancellableQuery<Tour> = this.generateAddOrUpdateTourQuery();
					await this.addTour(payload);
				} else {
					this.currentTour.tourSteps.forEach( (step, index) => {
						//PR-22322 Fix: error "Tour steps cannot share same step order"thrown by updateTour endpoint.
						step.stepOrder = index + 1;
					});

					const payload: CancellableQuery<Tour> = this.generateAddOrUpdateTourQuery();
					await this.updateTour(payload);
				}
			} catch {
				this.$notify({
					type: NotificationOptionsType.error,
					title: `${this.notifyTitleTourUpdate} - Error`,
					text: "Error returned while updating the Tour"
				});

				this.resetSaveTour();
				// do not close the modal.
				return;
			}

			this.resetSaveTour();
			this.close(true);
		}
	}

    @Watch("$v.$invalid")
    private evaluateSetupValid(): void{
        this.$emit('setup-valid', this.$v.$invalid);
	}

	@Watch("currentTour.tourSteps", { deep: true })
	private onTourStepsChanged(): void {
		// This will trigger when array items are modified (comments, duration, etc.)
		// Reset to first page if we end up with empty results due to pagination
		if (this.paginatedTourSteps.length === 0 && this.currentPage > 1) {
			this.currentPage = this.defaultPageNumber;
		}
	}

	@Watch("filteredTourSteps")
	private onFilteredStepsChanged(newVal: TourSteps[]): void {
		// If filtered results are less than current page shows, reset to page 1
		const startIndex = (this.currentPage - 1) * this.devicesTablePageSize;
		if (newVal.length > 0 && startIndex >= newVal.length) {
			this.currentPage = this.defaultPageNumber;
		}
	}

	/**
	 * When: no search on table, Then: return all the tourSteps in the current tour.
	 * When: search on table, Then: return the tourSteps in the current tour that match tableSearchValue.
	 */

	private get filteredTourSteps(): TourSteps[] {
		if (!this.tableSearchValue || this.tableSearchValue.trim() === '') {
			return this.currentTour.tourSteps;
		}

		const searchTerm = this.tableSearchValue.toLowerCase().trim();
		return this.currentTour.tourSteps.filter(step =>
			step.title?.toLowerCase().includes(searchTerm));
	}

	private get pendingTourDevicesCount(): number {
		return this.maxCamerasAllowedPerTour - this.currentTourStepsCount;
	}

	private get hasAvailableTourDeviceSlots(): boolean {
		return this.pendingTourDevicesCount >= 0;
	}
	private get totalRows(): number {
		return this.filteredTourSteps.length;
	}

	private get currentTourStepsCount(): number {
		return this.currentTour.tourSteps.length;
	}

	private get paginatedTourSteps(): TourSteps[] {
		const startIndex: number = (this.currentPage - 1) * this.devicesTablePageSize;
		const endIndex: number = startIndex + this.devicesTablePageSize;
		return this.filteredTourSteps.slice(startIndex, endIndex);
	}

	private get isUpdatingOrSavingTour(): boolean {
		return this.isLoadingMissingDevices || this.saveTourInProgress;
	}

	private get canAddMoreDevices(): boolean {
		if (this.pendingTourDevicesCount <= 0) {
			return false;
		}

		return true;
	}

	/** Filter through our current tour steps and if contains the same device, remove from the device list. */
	private get filteredDevices(): TourDevices[] {
		if (!this.canAddMoreDevices) {
			return [];
		}

		if (this.getSearchDevices.length > 0 && this.deviceSearchValue !== "") {
			return cloneDeep(this.getSearchDevices.filter(devices => {
				// Filter through the devices and only return the ones that hasn't been added already.
				return (this.currentTour && this.currentTour.tourSteps.find(tourStep => tourStep.objectID == devices.objectID)) ?  false : true;
				// Map the device object with the title containing the area e.g. Area - CameraName
			})).map(device =>  this.getDeviceArea(device));
		} else {
			return cloneDeep(this.getDeviceList.filter(devices => {
				// Filter through the devices and only return the ones that hasn't been added already.
				return  (this.currentTour && this.currentTour.tourSteps.find(tourStep => tourStep.objectID == devices.objectID)) ?  false : true;
				// Map the device object with the title containing the area e.g. Area - CameraName
				})).map(device => {return this.getDeviceArea(device);
			});
		}
	}

	private resetSaveTour(): void {
		this.saveTourInProgress = false;
		this.resetSearchDevices();
	}

	private resetSearchDevices(): void {
		this.deviceSearchValue = "";
		this.addSearchDevices([]);
	}

	private getDeviceArea(device: TourDevices): TourDevices {
		const group = this.areaList.find(area => area.groupID == device.groupId)
		return {
			title: `${ group ? group.title : "" } - ${ device.title }`,
			groupId: device.groupId,
			objectID: device.objectID
		} as TourDevices;
	}

	private resetCurrentTour(): void {
		this.currentTour.groupId = this.getUserTenantGroupId;
        this.currentTour.title = "";
        this.currentTour.tourId = 0;
        this.currentTour.tourOrder = 0;
        this.currentTour.tourSteps = [];
        this.currentTour.functionality = null;
        this.currentTour.tourStepsCount = 0;
        this.currentTour.matrixColumnSize = 3;
        this.currentTour.matrixRowSize = 3;
        this.currentTour.matrixOptions = defaultMatrixOption;
	}

	private cloneNewTourStepForTour(tourDevice: TourDevices): TourSteps {
		const tourStep: TourSteps = {
			tourStepId: 0,
			tourId: this.currentTour.tourId,
			objectID: tourDevice.objectID,
			title: tourDevice.title,
			stepOrder: 0,
			preset:0,
			duration: this.getLastStepDuration(),
		}
		return tourStep;
	}

	private removeDeviceFromTour(): void {
		this.currentTour.tourSteps.splice(this.deletingIndex, 1);
		this.deletingIndex = -1;
		this.deletingConfirmationIndex = -1;
	}

	private addDeviceToTour(device: TourDevices): void {
		// Check if the user is searching or not then use the correct array.
		if (this.deviceSearchValue) {
			const dev = this.getSearchDevices.find(x => x.objectID == device.objectID);
			const newTourStep = this.cloneNewTourStepForTour(dev);
			newTourStep.stepOrder = this.currentTour.tourSteps.length + 1;
			this.currentTour.tourSteps.push(newTourStep);
		} else {
			const dev = this.getDeviceList.find(x => x.objectID == device.objectID);
			const newTourStep = this.cloneNewTourStepForTour(dev);
			newTourStep.stepOrder = this.currentTour.tourSteps.length + 1;
			this.currentTour.tourSteps.push(newTourStep);
		}

		this.resetSearchDevices();
	}

	private getLastStepDuration(): number {
		if (this.currentTour.tourSteps.length > 0) {
			let lastDevice = this.currentTour.tourSteps[this.currentTour.tourSteps.length - 1];
			return lastDevice.duration;
		}
		return MIN_DURATION;
	}

	private generateQueryForGetMissingDevices(pageNumber: number, pageSize: number): PaginatedSearchRequest {
		let paginatedQueryParams: TourDevicesPaginatedQueryParams = {
			tourId: this.currentTour.tourId,
			groupId: this.searchableGroupId,
			page: pageNumber,
			pageSize: pageSize
		};

		return this.generateNewPaginatedSearchRequest(paginatedQueryParams);
	}

	private async getMissingDevices(
		page: number,
		pageSize: number
		): Promise<TourDevicesPagedResponse | null> {
		let cancellableQuery: PaginatedSearchRequest = this.generateQueryForGetMissingDevices(page, pageSize);

		try {
			const result: TourDevicesPagedResponse = await this.getMissingTourDevices(cancellableQuery);

			if (!result) {
				this.notifyTourDevicesUpdateError();
			}

			return result;
		} catch {
			this.notifyTourDevicesUpdateError();
		}

		return null;
	}

	private async getPendingTourDevices(): Promise<TourDevices[] | null> {
		let tourDevices: TourDevices [] = [] as TourDevices [];

		// get how many more devices we can add
		const pendingDevicesCount: number = this.pendingTourDevicesCount;
		// Create a Set of existing objectIDs for O(1) lookup
		const existingTourDeviceIds = new Set(this.currentTour.tourSteps.map(step => step.objectID));

		// First page request
		let pageSize = pendingDevicesCount > PageSize.Max ? PageSize.Max : pendingDevicesCount;
		let pageNumber = this.defaultPageNumber;

		const result:TourDevicesPagedResponse  = await this.getMissingDevices(pageNumber, pageSize);

		// got error
		if(!result) {
			return null;
		}

		const pagesCount = Math.ceil(result?.totalRecords / pageSize);
		const pendingTourDevices: TourDevices[] = result.data.filter(device => !existingTourDeviceIds.has(device.objectID));
		tourDevices.push(...pendingTourDevices);

		// If we've already collected enough devices or there's only one page, return what we have
		if (tourDevices.length >= pendingDevicesCount || pagesCount <= 1) {
			return tourDevices.slice(0, pendingDevicesCount);
		}

		// determine the required number of remaining devices
		let remainingNeeded = pendingDevicesCount - tourDevices.length;

		// Continue fetching until we have enough or we've gone through all pages
		while (remainingNeeded > 0 && pageNumber < pagesCount) {
			pageNumber++;
			// Get next  set of Tour Devices
			const nextResult:TourDevicesPagedResponse  = await this.getMissingDevices(pageNumber, pageSize);

			if(!nextResult) {
				return null;
			}

			const nextTourDevices: TourDevices[] = nextResult.data.filter(device => !existingTourDeviceIds.has(device.objectID));

			if (!nextTourDevices?.length) {
				// No more data
				break;
			}

			tourDevices.push(...nextTourDevices);
			remainingNeeded = pendingDevicesCount - tourDevices.length;
		}

		// Return only the number of devices we need
		return tourDevices.slice(0, pendingDevicesCount);
	}

	private async removeAllDevices(): Promise<void> {
		this.currentTour.tourSteps = [];
		this.resetDevicesTableSearchValue();
	}

	private debouncedAddAllDevices = debounce(async (): Promise<void> => this.addAllDevices(), getDebouncePeriod());

	private async addAllDevices(): Promise<void> {
		if(!this.canAddMoreDevices) {
			const message: string = "Tour has maximum number of devices allowed per Video Tour";
			this.notifyTourDevicesUpdate(NotificationOptionsType.warn, message);
			return;
		}

		this.isLoadingMissingDevices = true;
		try {
			const pendingTourDevices: TourDevices[] | null = await this.getPendingTourDevices();

			if (!pendingTourDevices) {
				// got null
				return;
			}

			pendingTourDevices.forEach((element) => {
				const newTourStep: TourSteps = this.cloneNewTourStepForTour(element);
				newTourStep.stepOrder = this.currentTour.tourSteps.length + 1;
				this.currentTour.tourSteps.push(newTourStep);
			});

			if (!pendingTourDevices.length) {
				const message: string = "No additional Tour Devices available for this tour";
				this.notifyTourDevicesUpdate(NotificationOptionsType.success, message);
			} else {
				const message: string = `Added ${pendingTourDevices.length} devices to this tour`;
				this.notifyTourDevicesUpdate(NotificationOptionsType.success, message);
			}

		} catch {
			this.notifyTourDevicesUpdateError();
		} finally {
			this.resetDevicesTableSearchValue();
			this.isLoadingMissingDevices = false;
		}
	}

	private debouncedHandleSearchDevice = debounce(async (searchTerm: string): Promise<void> => this.handleSearchDevice(searchTerm), getDebouncePeriod());

	private async handleSearchDevice(searchTerm: string): Promise<void> {
		let truncatedTerm: string = searchTerm;

		// Truncate the search term if it exceeds maximum length
		if (searchTerm.length > this.maxLengthTitle) {
			truncatedTerm = searchTerm.trim().substring(0, this.maxLengthTitle);
		}

		await this.performSearchDevice(truncatedTerm);
	}

	private async performSearchDevice(value: string): Promise<void> {
		if (!this.canAddMoreDevices) {
			return;
		}

		if (value == "") {
			this.addSearchDevices([]);
			return;
		}

		this.resetDevicesTableSearchValue();
		this.deviceSearchValue = value;

		const query: SearchDeviceListQueryParam = {
			groupId: this.searchableGroupId,
			searchTerm: value
		};

		const payload: CancellableQuery<SearchDeviceListQueryParam>
			= this.generateSearchDeviceListQuery(query);

		await this.searchDeviceList(payload);
	}

	private onMatrixInput(value: TourSetupMatrixOptions): void {
		this.currentTour.matrixColumnSize = value.matrixColumnSize;
		this.currentTour.matrixRowSize = value.matrixRowSize;
		this.currentTour.matrixOptions = value;
	}

	private updateDeleteIndex(index: number | null): void {
		const itemToDelete = this.paginatedTourSteps[index] ?? null;

		if (itemToDelete === null) {
			this.deletingConfirmationIndex = -1;
			this.deletingIndex = -1;
			return;
		}
		else
		{
			const itemFoundIndex = this.currentTour.tourSteps.findIndex((x) => x.objectID == itemToDelete.objectID);

			this.deletingConfirmationIndex = index;
			this.deletingIndex = itemFoundIndex;
		}

		// If deleting the last item on a page, go to the previous page (except on page 1)
		if (index !== null && this.paginatedTourSteps.length === 1 && this.currentPage > 1) {
			this.currentPage--;
		}
	}

	private onRecordsPageSizeClick(): void {
		this.devicesTablePageSize = parseInt(this.devicesTablePageSizeUpdated);
	}

	private getValidationErrors(index: number): string[] {
		const errors = [];
		const actualIndex = ((this.currentPage-1) * this.devicesTablePageSize) + index;

		if (this.$v.currentTour.tourSteps.$each.$iter[actualIndex]) {
			const validation = this.$v.currentTour.tourSteps.$each.$iter[actualIndex].duration;

			if (!validation.required) errors.push("Duration is required");
			if (!validation.min) errors.push(`Duration should be greater than or equal ${validation.$params.min.value}`);
			if (!validation.max) errors.push(`Duration should be less than or equal ${validation.$params.max.value}`);
		}

		return errors;
	}

	private formatDevicesTablePageSizeInput(value: string): string {
		return removeNonDigitsFromString(value);
	}

	private handleSearchInput = debounce(async (searchTerm: string): Promise<void> => {
		let truncatedTerm: string = searchTerm;

		// Truncate the search term if it exceeds maximum length
		if (searchTerm.length > this.maxLengthTitle) {
			truncatedTerm = searchTerm.trim().substring(0, this.maxLengthTitle);
		}

		await this.performSearch(truncatedTerm);
	}, getDebouncePeriod());

	private async performSearch(searchTerm: string): Promise<void> {
		this.tableSearchValue = searchTerm;
		 // Reset to page 1 when search changes to avoid empty results.
		this.currentPage = 1;
	}

	private resetDevicesTableSearchValue(): void {
		this.tableSearchValue = "";
		this.devicesTablePageSizeUpdated = this.recordsPageSizeDefault;
		this.currentPage = this.defaultPageNumber;
	}

	private notifyTourDevicesUpdate(notifyType: NotificationOptionsType, message: string) {
		this.$notify({
			type: notifyType,
			title: this.notifyTitleTourDeviceUpdate,
			text: message
		});
	}

	private notifyTourDevicesUpdateError(): void {
		const notifyAddTourDevicesError: string = "Unable to add devices for this tour";
		this.$notify({
			type: NotificationOptionsType.error,
			title: this.notifyTitleTourDeviceUpdateError,
			text: notifyAddTourDevicesError
		});
	}

	private generateSearchDeviceListQuery(query: SearchDeviceListQueryParam): CancellableQuery<SearchDeviceListQueryParam> {
		this.searchDeviceListCancelTokenSource = getNewCancelTokenSource(
			this.searchDeviceListCancelTokenSource, true);

		return {
			params: query,
			cancelTokenSource: this.searchDeviceListCancelTokenSource
		} as CancellableQuery<SearchDeviceListQueryParam>;
	}

	private generateAddOrUpdateTourQuery(): CancellableQuery<Tour> {
		this.addOrUpdateTourCancelTokenSource = getNewCancelTokenSource(
			this.addOrUpdateTourCancelTokenSource, true);

		return {
			params: this.currentTour,
			cancelTokenSource: this.addOrUpdateTourCancelTokenSource
		} as CancellableQuery<Tour>;
	}

	private onDragEnd() : void {
		this.refreshOrder();
	}

	private refreshOrder() : void {
		this.currentTour.tourSteps
			.forEach((x, index) => x.stepOrder = index + 1);
	}

	private stepOrderChange(item: TourSteps) : void {
		this.currentTour.tourSteps.remove(item);
		this.currentTour.tourSteps.splice(item.stepOrder - 1, 0, item);
		this.refreshOrder();
	}
}
